import { EditPencil as EditPencilIcon, UserXmark as UserXMarkIcon } from 'iconoir-react'
import { isEmpty, startCase } from 'lodash'
import { ReactElement, useState } from 'react'
import { ClosedLostForm } from '../ClosedLostForm'
import { RichTextEditor } from '../editor/RichTextEditor'
import NameIcon from '../shared/NameIcon'
import { Tooltip } from '../Tooltip'
import { ActivityCard } from './ActivityCard'
import { KeyValueDrawer } from './KeyValueDrawer'
import type { ActivityJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError } from 'app/services/notify.client'
import { assertGood } from 'lib/assertBoom'
import { logWarn } from 'lib/logging'

type Props = {
  activity: ActivityJson
  alwaysOpen: boolean
  contact?: ContactJson
  isFirst?: boolean
  isLast?: boolean
  handleEdit?: (activityId: string, values: any, onSuccess: () => void) => Promise<void>
}

export function ClosedLostCard({ activity, alwaysOpen, contact, isFirst, isLast, handleEdit }: Props): ReactElement {
  assertGood(activity.type === `Milestone`, `Invalid activity type`)
  const [nameTooltip, setNameTooltip] = useState<string | undefined>(undefined)
  const displayName = startCase(activity.owner?.name || activity?.source || `-`)
  const closedLostReason =
    typeof activity?.data?.closedLostReason === `object`
      ? activity.data.closedLostReason?.to
      : activity?.data?.closedLostReason

  return (
    <ActivityCard
      icon={<UserXMarkIcon className="h-5 w-5 rounded-md bg-red-600 p-1 text-on-color" />}
      actions={
        <div className="flex gap-x-1 p-0.5">
          <Tooltip message="Edit Closed Lost Note">
            <EditPencilIcon
              className="h-3 w-3 cursor-pointer"
              strokeWidth={1}
              aria-label="Edit Closed Lost Note"
              onClick={() => {
                if (!contact) {
                  logWarn(`Contact not found for Closed Lost activity`)
                  notifyError(`Contact not found for Closed Lost activity`)
                  return
                }
                showModal(
                  <ClosedLostForm
                    activity={activity}
                    contact={contact}
                    handleSubmit={async (data) => {
                      if (isEmpty(data)) {
                        logWarn(`Empty data for Closed Lost activity`)
                        return
                      }
                      await handleEdit?.(activity.id!, data, closeModal)
                    }}
                    handleCancel={closeModal}
                  />,
                  {
                    title: `Edit Closed Lost Note`,
                    isFullScreen: false,
                    useRadix: true,
                  },
                )
              }}
            />
          </Tooltip>
        </div>
      }
      title={
        <div className="flex min-w-0 items-center gap-x-1 truncate text-ui-sm text-strong">
          Closed Lost<span className="text-body-sm text-subtle">{` by `}</span>
          <NameIcon
            widthClassName="w-5"
            heightClassName="h-5"
            backgroundClassName="bg-surface-strong border border-default rounded-full"
            textClassName="text-strong"
            fullName={`${activity.owner?.name?.[0] || activity?.source?.[0] || `-`}`}
            sizeStyle={{ fontSize: `8px` }}
          />
          <Tooltip
            className="min-w-0 truncate"
            tooltipClassName="max-w-xs break-words rounded-md bg-inverse px-2 py-1 text-ui-xs text-inverse"
            message={nameTooltip}
            onMouseOver={(event) => {
              const el = event.currentTarget as HTMLElement
              const isOverflowing = el.scrollWidth > el.clientWidth
              setNameTooltip(isOverflowing ? displayName : undefined)
            }}
          >
            {displayName}
          </Tooltip>
        </div>
      }
      activityDate={activity?.activityDate}
      tags={
        <span className="rounded border bg-red-100 px-1.5 text-xs font-medium not-italic leading-4 text-red-800">
          {closedLostReason ?? contact?.closedLostReason}
        </span>
      }
      preview={
        <div className="w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          <KeyValueDrawer
            map={{
              'Closed Lost Reason': closedLostReason || contact?.closedLostReason || `-`,
              'Qualification Status':
                activity?.isQualified === true ? `Qualified` : activity?.isQualified === false ? `Disqualified` : `-`,
            }}
          />
        </div>
      }
      drawer={
        <div
          key={activity?.id}
          className="w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm"
        >
          <>
            <KeyValueDrawer
              map={{
                'Closed Lost Reason': closedLostReason || contact?.closedLostReason || `-`,
                'Qualification Status':
                  activity?.isQualified === true ? `Qualified` : activity?.isQualified === false ? `Disqualified` : `-`,
              }}
            />
            <div key={`${activity?.id}-${activity?.note}`} className="flex">
              <div className="mt-3 flex grow flex-col gap-y-0.5">
                <span className="text-body-xs text-subtle">Note</span>
                <RichTextEditor namespace="Edit-Closed-Lost-Note" text={activity?.note || `-`} editable={false} />
              </div>
            </div>
          </>
        </div>
      }
      alwaysOpen={alwaysOpen}
      isFirst={isFirst}
      isLast={isLast}
    />
  )
}
